import {
  BaseDecisionPriority,
  DecisionContext,
  DecisionResult,
  MoveInfo,
} from "../../interfaces/DecisionPriority";
import { IPiece, Position, PieceType } from "../../../../types";
import {
  MCTSUtils_v2,
  cloneFullGameState,
  getPieceFromPosition,
  applyMoveWithSnakeRespawn,
} from "../../../utils/MCTSUtils";
import { MCTSMoveGenerationService } from "../../../utils/MCTSMoveGenerationService";

/**
 * Priority 13: Two Turn King Capture
 *
 * OFFENSIVE: If we can DEFINITELY force a king capture within 2 moves,
 * regardless of the enemy's response, force the first step immediately.
 *
 * Algorithm (4-ply centered on enemy king):
 *   1. For each of our moves M (filtered to near enemy king):
 *      a. Simulate M
 *      b. For EVERY possible enemy response E:
 *         - Simulate E
 *         - Can we capture the enemy king?
 *      c. If ALL enemy responses allow king capture → M is forced win → EARLY EXIT
 */
export class TwoTurnKingCapturePriority extends BaseDecisionPriority {
  readonly priority = 13;
  readonly name = "Two Turn King Capture";
  readonly description =
    "Force king capture within 2 moves, even at material cost";

  private analysisLogs: string[] = [];

  isRelevant(context: DecisionContext): boolean {
    // Quick check: do we have any pieces near the enemy king?
    const enemyKingPos = this.findEnemyKing(
      context.gameState,
      context.gameState.isLightTurn
    );
    if (!enemyKingPos) return false;

    // Only relevant if we have moves in the vicinity of the enemy king
    return context.allPossibleMoves.some((m) => {
      const toDist =
        Math.abs(m.move.position.row - enemyKingPos.row) +
        Math.abs(m.move.position.col - enemyKingPos.col);
      const fromDist =
        Math.abs(m.fromPosition.row - enemyKingPos.row) +
        Math.abs(m.fromPosition.col - enemyKingPos.col);
      return toDist <= 3 || fromDist <= 3;
    });
  }

  analyze(context: DecisionContext): DecisionResult {
    this.analysisLogs = [];

    const gameState = context.gameState;
    const isPlayerLight = gameState.isLightTurn;
    const moveService = MCTSMoveGenerationService.getInstance();

    const enemyKingPos = this.findEnemyKing(gameState, isPlayerLight);
    if (!enemyKingPos) {
      return this.createNoActionResult("No enemy king found");
    }

    this.addLog(
      `🎯 Scanning for 2-move forced king capture. Enemy king at [${enemyKingPos.row},${enemyKingPos.col}]`
    );

    // Pre-filter: only check moves near enemy king (landing or originating within 3 squares)
    const candidateMoves = context.allPossibleMoves.filter((m) => {
      const toDist =
        Math.abs(m.move.position.row - enemyKingPos.row) +
        Math.abs(m.move.position.col - enemyKingPos.col);
      const fromDist =
        Math.abs(m.fromPosition.row - enemyKingPos.row) +
        Math.abs(m.fromPosition.col - enemyKingPos.col);
      return toDist <= 3 || fromDist <= 3;
    });

    this.addLog(`🔍 Testing ${candidateMoves.length} candidate moves near enemy king`);

    for (const ourMove of candidateMoves) {
      // Simulate our move
      const stateAfterOur = cloneFullGameState(gameState);
      const ourPiece = getPieceFromPosition(stateAfterOur, ourMove.fromPosition);
      if (!ourPiece) continue;

      try {
        applyMoveWithSnakeRespawn(
          stateAfterOur,
          ourMove.fromPosition,
          ourMove.move as any,
          ourPiece
        );
      } catch {
        continue;
      }

      // Find enemy king in new state
      const enemyKingAfterOur = this.findEnemyKing(stateAfterOur, isPlayerLight);
      if (!enemyKingAfterOur) continue; // king captured by our move — Priority 2 handles this

      // Generate all enemy responses
      stateAfterOur.isLightTurn = !isPlayerLight;
      const enemyResponses = moveService.getAllMoves(stateAfterOur);

      if (enemyResponses.length === 0) continue;

      // Check: for EVERY enemy response, can we capture the king afterward?
      let allResponsesLeadToCapture = true;

      for (const enemyResponse of enemyResponses) {
        const stateAfterEnemy = cloneFullGameState(stateAfterOur);
        const enemyPc = getPieceFromPosition(
          stateAfterEnemy,
          enemyResponse.fromPosition
        );
        if (!enemyPc) {
          allResponsesLeadToCapture = false;
          break;
        }

        try {
          applyMoveWithSnakeRespawn(
            stateAfterEnemy,
            enemyResponse.fromPosition,
            enemyResponse.move as any,
            enemyPc
          );
        } catch {
          allResponsesLeadToCapture = false;
          break;
        }

        // Our turn — can we capture the enemy king?
        stateAfterEnemy.isLightTurn = isPlayerLight;
        const ourCaptureMoves = moveService.getAllMoves(stateAfterEnemy);

        const enemyKingNow = this.findEnemyKing(stateAfterEnemy, isPlayerLight);
        if (!enemyKingNow) {
          // King disappeared — might have been captured as part of enemy move (shouldn't happen)
          continue;
        }

        const canCapture = ourCaptureMoves.some(
          (m) =>
            m.move.position.row === enemyKingNow.row &&
            m.move.position.col === enemyKingNow.col
        );

        if (!canCapture) {
          allResponsesLeadToCapture = false;
          break; // Early exit — one enemy escape found
        }
      }

      if (allResponsesLeadToCapture) {
        const moveDesc = `[${ourMove.fromPosition.row},${ourMove.fromPosition.col}]->[${ourMove.move.position.row},${ourMove.move.position.col}]`;
        this.addLog(
          `🎯 FORCED 2-MOVE KING CAPTURE: ${moveDesc} — enemy has NO response that prevents capture!`
        );

        return this.createEarlyExitResult(
          ourMove,
          `${this.name}: Forced king capture in 2 moves via ${moveDesc}`,
          {
            forcedCapture: true,
            testedMoves: candidateMoves.length,
            logs: this.analysisLogs,
          }
        );
      }
    }

    this.addLog("✅ No forced 2-move king capture found");
    return {
      shouldEarlyExit: false,
      reasoning: "No forced 2-move king capture available",
      priority: this.priority,
      diagnostics: { logs: this.analysisLogs },
    };
  }

  private addLog(message: string): void {
    this.analysisLogs.push(message);
  }

  /** Find the enemy king position */
  private findEnemyKing(
    gameState: any,
    isPlayerLight: boolean
  ): Position | null {
    const board = gameState.board;
    for (let row = 0; row < board.length; row++) {
      for (let col = 0; col < board[row].length; col++) {
        const piece = MCTSUtils_v2.getPieceAt(board, { row, col });
        if (piece && piece.isKing && piece.isLight !== isPlayerLight) {
          return { row, col };
        }
      }
    }
    return null;
  }
}
